/**
 * Real spoken narration via the browser's built-in Web Speech API.
 * Client-side, free, no TTS provider or audio storage. Drives the avatar's
 * talking state via onStart/onEnd. A premium server-side TTS can layer on later.
 */

let preferredVoice: SpeechSynthesisVoice | null = null;

export function isSpeechSupported(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
}

function pickVoice(): SpeechSynthesisVoice | null {
  if (!isSpeechSupported()) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const en = voices.filter((v) => /^en/i.test(v.lang));
  const nice =
    en.find((v) => /google us english|samantha|jenny|aria|natural|zira|female/i.test(v.name)) ||
    en.find((v) => /google/i.test(v.name)) ||
    en[0] ||
    voices[0];
  return nice ?? null;
}

if (isSpeechSupported()) {
  // Voices can load asynchronously.
  preferredVoice = pickVoice();
  window.speechSynthesis.onvoiceschanged = () => {
    preferredVoice = pickVoice();
  };
}

export interface SpeakHandlers {
  onStart?: () => void;
  onEnd?: () => void;
}

export function speak(text: string, handlers: SpeakHandlers = {}): void {
  if (!isSpeechSupported() || !text || !text.trim()) return;
  const ss = window.speechSynthesis;
  ss.cancel(); // stop anything currently speaking
  const u = new SpeechSynthesisUtterance(text);
  if (!preferredVoice) preferredVoice = pickVoice();
  if (preferredVoice) u.voice = preferredVoice;
  u.rate = 0.97; // slightly slower — clearer for learning
  u.pitch = 1.12; // a touch higher — friendly/kid-warm
  u.volume = 1;
  u.onstart = () => handlers.onStart?.();
  u.onend = () => handlers.onEnd?.();
  u.onerror = () => handlers.onEnd?.();
  ss.speak(u);
}

export function cancelSpeech(): void {
  if (isSpeechSupported()) window.speechSynthesis.cancel();
}

export function pauseSpeech(): void {
  if (!isSpeechSupported()) return;
  const ss = window.speechSynthesis;
  if (ss.speaking && !ss.paused) ss.pause();
}

export function resumeSpeech(): void {
  if (!isSpeechSupported()) return;
  const ss = window.speechSynthesis;
  if (ss.paused) ss.resume();
}

/** Estimate seconds to speak `text` at our rate (~2.6 words/sec). */
export function estimateSpeechSeconds(text?: string): number {
  if (!text) return 0;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return words / 2.6;
}
